import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/config';
import { doc, getDoc, collection, onSnapshot, query, where, orderBy, updateDoc, serverTimestamp } from 'firebase/firestore';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { sendNotificationEmail, leaveCancellationTemplate } from '../../firebase/notifications';

// --- Cancellation Modal Component ---
function CancellationModal({ request, onCancel, onConfirm }) {
    const [reason, setReason] = useState('');
    const isPostDated = new Date() > request.startDate.toDate();

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Confirm Leave Cancellation</h2>
                {isPostDated && (
                    <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4" role="alert">
                        <p className="font-bold">Warning</p>
                        <p>You are cancelling a leave request after its start date has passed. Your manager will be notified.</p>
                    </div>
                )}
                <p className="text-gray-600 mb-4">Please provide a reason for cancelling your {request.leaveType} from {request.startDate.toDate().toLocaleDateString()} to {request.endDate.toDate().toLocaleDateString()}.</p>
                <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows="3"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="Reason for cancellation..."
                    required
                ></textarea>
                <div className="flex justify-end space-x-4 mt-6">
                    <button onClick={onCancel} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">Close</button>
                    <button onClick={() => onConfirm(reason)} disabled={!reason} className="px-6 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 disabled:bg-red-300">Confirm Cancellation</button>
                </div>
            </div>
        </div>
    );
}

export default function MyLeaveRequests({ userProfile, appId }) {
    const [myRequests, setMyRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [requestToCancel, setRequestToCancel] = useState(null);

    useEffect(() => {
        const requestsQuery = query(
            collection(db, `/artifacts/${appId}/public/data/leaveRequests`),
            where("requesterId", "==", userProfile.uid),
            orderBy('createdAt', 'desc')
        );
        const unsub = onSnapshot(requestsQuery, (snapshot) => {
            setMyRequests(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setIsLoading(false); 
        });
        return () => unsub();
    }, [appId, userProfile.uid]);

    const sortedRequests = useMemo(() => {
        let sortableItems = [...myRequests];
        sortableItems.sort((a, b) => {
            let aValue = a[sortConfig.key]?.toDate ? a[sortConfig.key].toDate() : a[sortConfig.key];
            let bValue = b[sortConfig.key]?.toDate ? b[sortConfig.key].toDate() : b[sortConfig.key];
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return sortableItems;
    }, [myRequests, sortConfig]);

    const paginatedRequests = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedRequests.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedRequests, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(sortedRequests.length / itemsPerPage);
    
    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) setCurrentPage(newPage);
    };
    
    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const handleOpenCancelModal = (request) => {
        setRequestToCancel(request);
        setIsCancelModalOpen(true);
    };

    const handleConfirmCancel = async (reason) => {
        if (!requestToCancel || !reason) return;
        const requestRef = doc(db, `/artifacts/${appId}/public/data/leaveRequests`, requestToCancel.id);
        const isPostDated = new Date() > requestToCancel.startDate.toDate();
        try {
            await updateDoc(requestRef, {
                status: 'Cancellation Requested',
                cancellationReason: reason,
                updatedAt: serverTimestamp()
            });
            if (userProfile.managerId) {
                const managerRef = doc(db, `/artifacts/${appId}/users`, userProfile.managerId);
                const managerSnap = await getDoc(managerRef);
                if (managerSnap.exists() && managerSnap.data().email) {
                    const emailContent = leaveCancellationTemplate(requestToCancel, userProfile, reason, isPostDated);
                    await sendNotificationEmail({ to: [managerSnap.data().email], ...emailContent, appId });
                }
            }
        } catch (error) {
            console.error("Error cancelling request:", error);
        } finally {
            setIsCancelModalOpen(false);
            setRequestToCancel(null);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Pending Manager Approval': return 'bg-yellow-100 text-yellow-800';
            case 'Approved': return 'bg-green-100 text-green-800';
            case 'Rejected': return 'bg-red-100 text-red-800';
            case 'Cancellation Requested': return 'bg-orange-100 text-orange-800';
            case 'Cancelled': return 'bg-gray-200 text-gray-700';
            default: return 'bg-gray-100 text-gray-800';
        }
    };
    
    if (isLoading) return <LoadingSpinner />;

    return (
        <>
            {isCancelModalOpen && (
                <CancellationModal
                    request={requestToCancel}
                    onCancel={() => setIsCancelModalOpen(false)}
                    onConfirm={handleConfirmCancel}
                />
            )}
            <div className="bg-white p-8 rounded-lg shadow-md">
                <h2 className="text-2xl font-semibold text-[#213368] mb-4">My Leave Requests</h2>
                <div className="pt-6">
                    {myRequests.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                            <p>You have not submitted any leave requests yet.</p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leave Type</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applied On</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {paginatedRequests.map(req => (
                                            <tr key={req.id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{req.leaveType}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">{req.startDate.toDate().toLocaleDateString()} - {req.endDate.toDate().toLocaleDateString()}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">{req.duration}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">{req.createdAt.toDate().toLocaleDateString()}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(req.status)}`}>
                                                        {req.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    { (req.status === 'Pending Manager Approval' || req.status === 'Approved') && (
                                                        <button onClick={() => handleOpenCancelModal(req)} className="text-red-600 hover:text-red-800 text-xs">Cancel</button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {totalPages > 1 && (
                                <div className="flex justify-between items-center mt-4">
                                    <div className="flex items-center space-x-2 text-sm">
                                        <span>Items per page:</span>
                                        <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="border border-gray-300 rounded-md p-1">
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md disabled:opacity-50">
                                            Previous
                                        </button>
                                        <span className="text-sm">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded-md disabled:opacity-50">
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

